import { Trash2, RotateCcw, Clock } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/Button';
import type { Case } from '../../types';

interface DeletedCaseCardProps {
  caseData: Case;
  onRestore: (caseId: string) => void;
  onPermanentDelete: (caseId: string) => void;
}

export function DeletedCaseCard({ caseData, onRestore, onPermanentDelete }: DeletedCaseCardProps) {
  const daysSinceDeleted = caseData.deleted_at
    ? Math.floor((Date.now() - new Date(caseData.deleted_at).getTime()) / (1000 * 60 * 60 * 24))
    : 0;

  const daysRemaining = Math.max(0, 14 - daysSinceDeleted);

  return (
    <Card className="opacity-75 overflow-hidden">
      <CardHeader className="pb-3 overflow-hidden">
        <div className="flex items-start justify-between gap-2 min-w-0 overflow-hidden">
          <CardTitle className="text-base font-semibold truncate" title={caseData.name}>
            {caseData.name}
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span>Deleted {daysSinceDeleted} day{daysSinceDeleted !== 1 ? 's' : ''} ago</span>
          </div>
          {daysRemaining <= 3 && (
            <p className="text-xs text-red-500 font-medium">
              {daysRemaining === 0
                ? 'Will be permanently deleted soon'
                : `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining before permanent deletion`}
            </p>
          )}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onRestore(caseData.id)}
              className="flex-1"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
              Restore
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPermanentDelete(caseData.id)}
              className="flex-1 text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 className="h-3.5 w-3.5 mr-1.5" />
              Delete Forever
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
